import React from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/svg/auth/logo.svg";
import { NAV_ICONS, NavIcon } from "./navIcons";
import { useActivePath } from "./activePath";
import { useNavigationGuard } from "./UnsavedChanges";
import { useAccountSummary } from "./accountSummary";
import { ROUTES } from "../../routes/paths";
import TrialMeter from "../../features/billing/components/TrialMeter";
import TrialBanner from "../../features/billing/components/TrialBanner";
// Deep imports rather than the feature barrels: those pull in every enquiry
// and client screen, and the rail only wants a number from each.
import { useNewEnquiryCount } from "../../features/enquiries/hooks/useNewEnquiryCount";
import { useNewClientCount } from "../../features/clients/hooks/useNewClientCount";
import { MAX_SHOWN_COUNT } from "../../lib/liveNews";
import { NewTag } from "../ui";

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  /**
   * How many things behind this item are waiting to be looked at, if it is the
   * kind of item that counts anything. Zero draws nothing — a badge saying "0"
   * is a worse answer than no badge, since it takes up the same space to say
   * there is nothing to say.
   */
  count?: number;
  onClick: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  active,
  count = 0,
  onClick,
}) => (
  <button
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    // Text and icon keep the same ink in both states — only the pill's fill
    // and hairline border mark the active item. The inactive border is
    // transparent rather than absent so nothing shifts by half a pixel.
    className={`w-full flex items-center gap-3 px-3 py-2.5 3xl:px-4 3xl:py-3 rounded-[6px] text-[14px] 3xl:text-[16px] text-[#00424B] border-[0.5px] transition-colors ${
      active
        ? "bg-[#EFFBFD] border-[#5BD3E1] font-medium"
        : "border-transparent hover:bg-[#EFFBFD]/60"
    }`}
  >
    <span className="w-[18px] h-[18px] 3xl:w-[20px] 3xl:h-[20px] flex-shrink-0">
      {icon}
    </span>
    <span className="truncate">{label}</span>
    {/* Pushed to the far edge rather than sitting after the label, so the
        badges on a rail of items line up with each other instead of with the
        ends of the words. Red rather than the row tag's cyan, and still where
        that one catches the light: on a rail the eye passes over on the way to
        somewhere else, the colour is enough, and the row it leads to is where
        the flag can move. Capped, because past a point the digit has stopped
        being the news — see `useNewEnquiryCount`. */}
    {count > 0 && (
      <NewTag
        tone="count"
        className="ml-auto"
        label={`${count} new`}
      >
        {count > MAX_SHOWN_COUNT ? `${MAX_SHOWN_COUNT}+` : count}
      </NewTag>
    )}
  </button>
);

interface NavItem {
  path: string;
  label: string;
  /** SVGR component or a lucide icon - both take a className to size them. */
  Icon: NavIcon;
  /** What the item's badge shows, for the one item that carries one. */
  count?: number;
}

const Separator = () => (
  <div className="my-3 mx-1 border-t border-[#D7F9FD]" />
);

/**
 * The rail, from `md` up — and only from `md` up.
 *
 * It used to be an off-canvas drawer below that as well, which meant a phone
 * was handed the same column of nine equal items a desktop gets, opened over
 * the page. What a phone gets now is the tab bar across its foot and
 * `MobileAccountMenu` behind its avatar: the four commonest sections always on
 * screen, and the rest grouped under the account they belong to.
 */
const Sidebar: React.FC = () => {
  const navigate = useNavigate();

  // A rail item is the commonest way somebody leaves a half-filled form by
  // accident — it is a press away from anywhere in the app, and nothing on it
  // says the screen behind it is holding anything. So the navigation is handed
  // over rather than made: it happens straight away where there is nothing to
  // lose, and is asked about first where there is. See `UnsavedChanges`.
  const guardNavigation = useNavigationGuard();

  // Who is signed in, what they are on, and how much of the trial is left.
  // The same hook the phone's account menu reads, so the two navigations name
  // one account the same way — see `accountSummary`.
  const { displayName, imageUrl, accountLine, isSubscribed, trial, lapse } =
    useAccountSummary();

  // How many enquiries nobody has opened yet. The rail is the one place in the
  // app that says so without being on the list — which is the point: an
  // enquiry that has come in is news wherever the dietitian happens to be
  // working. Read here rather than on the list itself, so it is counted once
  // for the whole session. See `useNewEnquiryCount`.
  const newEnquiries = useNewEnquiryCount();

  // And how much there is to look at among the clients: one who has landed
  // since this browser started counting and nobody has opened, and answers
  // that have come back unread. Two arrivals under one number, because to the
  // person reading the rail they are the same errand — there is a client to go
  // and look at — and both are counted, so a form coming back moves the badge
  // even for a client it was already counting. See `useNewClientCount`.
  const newClients = useNewClientCount();

  // Active item is derived from the URL, so deep links and browser history
  // highlight correctly without any extra state to keep in sync. Where a
  // navigation said which section it came from, that wins — see
  // `useActivePath`, which the phone's tab bar reads too.
  const activePath = useActivePath();
  const isActive = (path: string) => activePath.startsWith(path);

  const iconClass = "w-full h-full";

  // Glyphs come from `NAV_ICONS`, which the not-yet-built screens read too, so
  // a destination is known by the same icon in the rail and on its own page.
  const items: (NavItem | "separator")[] = [
    {
      path: ROUTES.dashboard,
      label: "Dashboard",
      Icon: NAV_ICONS[ROUTES.dashboard],
    },
    // Directly under the dashboard, which is where the design puts it and
    // where it belongs: the dashboard says how the practice is doing, and this
    // says what the practitioner is doing today. Everything below the two is a
    // record to go and work on.
    {
      path: ROUTES.myTask,
      label: "My task",
      Icon: NAV_ICONS[ROUTES.myTask],
    },
    {
      path: ROUTES.myClients,
      label: "Clients",
      Icon: NAV_ICONS[ROUTES.myClients],
      count: newClients,
    },
    {
      path: ROUTES.enquiries,
      label: "Enquiries",
      Icon: NAV_ICONS[ROUTES.enquiries],
      count: newEnquiries,
    },
    // No longer cut on a phone: the rail itself is gone below `md`, so the
    // one screen a phone cannot carry is simply not among the destinations
    // the account menu offers. See the matching cut on the client's Plans tab.
    {
      path: ROUTES.createPlan,
      label: "Create plan",
      Icon: NAV_ICONS[ROUTES.createPlan],
    },
    {
      path: ROUTES.dishes,
      label: "Dishes",
      Icon: NAV_ICONS[ROUTES.dishes],
    },
    {
      path: ROUTES.revenue,
      label: "Revenue",
      Icon: NAV_ICONS[ROUTES.revenue],
    },
    "separator",
    {
      path: ROUTES.myBrand,
      label: "My brand",
      Icon: NAV_ICONS[ROUTES.myBrand],
    },
    {
      path: ROUTES.createPage,
      label: "Create page",
      Icon: NAV_ICONS[ROUTES.createPage],
    },
    {
      path: ROUTES.launchApp,
      label: "Launch app",
      Icon: NAV_ICONS[ROUTES.launchApp],
    },
    "separator",
    // Billing used to sit on its own between two rules here. It is a tab of
    // Settings now — what the practice pays Naurist is settled in the same
    // place as everything else about the account, rather than from a rail item
    // of its own.
    {
      path: ROUTES.settings,
      label: "Settings",
      Icon: NAV_ICONS[ROUTES.settings],
    },
  ];

  return (
    // `hidden md:flex` rather than an off-canvas transform: below `md` there is
    // no drawer to slide, so the rail is not rendered into the layout at all.
    <aside
      className="hidden bg-[#F8FEFF] border-r-[0.5px] border-[#A6EDF5] overflow-hidden
        md:flex md:flex-col md:flex-shrink-0 md:h-full
        md:w-[180px] lg:w-[192px] 2xl:w-[210px] 3xl:w-[240px] 4xl:w-[280px]"
    >
      {/* The rail sits inside the page from `md` up, so its head takes plain
          padding — the status bar and the camera are the phone's top bar's
          to clear, and this is never on a phone. */}
      <div className="flex-shrink-0 flex items-center px-5 3xl:px-6 pb-2 pt-6">
        <img src={logo} alt="Naurist" className="h-6 3xl:h-7 w-auto" />
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto sidebar-nav-scroll px-3 3xl:px-4 mt-4 pb-2">
        <div className="flex flex-col gap-1">
          {items.map((item, index) =>
            item === "separator" ? (
              <Separator key={`sep-${index}`} />
            ) : (
              <SidebarItem
                key={item.path}
                icon={<item.Icon className={iconClass} />}
                label={item.label}
                active={isActive(item.path)}
                count={item.count}
                // Guarded even where the item is the lit one: a form lives
                // under the section it belongs to, so Add client is reached
                // with My clients already active, and pressing it is still
                // leaving the form.
                onClick={() => guardNavigation(() => navigate(item.path))}
              />
            )
          )}
        </div>
      </nav>

      {/* Account block, pinned to the bottom: who is signed in, and what
          they are on. It carries no action of its own — signing out is on
          the Settings screen's Accounts tab, with the rest of what can be
          done to an account. */}
      <div className="flex-shrink-0 px-4 3xl:px-5 pt-4 pb-4">
        {/* How much of the free trial is left, over the account it belongs
            to — or, at the end of it, the way onto a plan.

            Both are drawn and exactly one of them appears: the meter holds
            the calm stretch of the fortnight and stands down for the last
            few days, where the card takes over and stays past the end date.
            Which day that is is `TRIAL_URGENT_DAYS`, and neither component
            decides it alone. Only once the billing read has answered, either
            way — `accountSummary` is what holds them to that, and holds the
            phone's account menu to the same. A workspace on a paid plan has
            neither to draw and sees nothing here. */}
        <TrialMeter trial={trial} />
        <TrialBanner trial={trial} ended={lapse} />

        <div className="flex items-center gap-3">
          <div className="w-9 h-9 3xl:w-10 3xl:h-10 rounded-full bg-[#EDE7F6] flex items-center justify-center flex-shrink-0 overflow-hidden">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-sm 3xl:text-base font-semibold text-[#5B3FA8]">
                {displayName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] 3xl:text-[15px] font-medium text-ink truncate">
              {displayName}
            </div>
            {/* The tier, or the address — see `accountLine`. The tier
                keeps the brand teal it has always had; the address is grey
                and quiet, since it names the account rather than telling
                them anything about it, and the name above is what the eye
                should land on. Neither leads anywhere: both are facts. A
                non-breaking space where there is nothing to print, so the
                block keeps its height either way. */}
            <div
              className={`text-[11px] 3xl:text-[13px] truncate ${
                isSubscribed ? "text-[#00BACB] font-medium" : "text-[#8B8D98]"
              }`}
            >
              {accountLine || "\u00a0"}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
